import React from 'react'
import styles from './SegmentPopup.scss'
import { Popup } from 'common/Map/Popup'
import { Distance, Elevation, Duration, Grade } from 'common/units'
import { northernmostPoint } from 'lib/geoUtils'
import { Msg, useFormatMessage, featureNames, commonNouns, commonMetrics, commonMessages } from 'lib/localization'
import _ from 'lodash'
import { defineMessages } from 'react-intl'

const messages = defineMessages({
  selected: {
    id: 'SegmentPopup.selected',
    defaultMessage: 'Selected'
  }
})

// desired close behavior
// - closing the popup with the close button hides it while moving/resizing the selection
// - however, making a new selection by dragging should re-show the popup
// - this means we're going to need a new set of callbacks from the Map to differentiate
//   between segment *adjustement* and *new selection actions*

export class SegmentPopup extends React.PureComponent {
  constructor (props) {
    super(props)
    this._throttledCalculateMetrics = _.throttle(this.calculateMetrics, 500)
    this.state = {
      metrics: null
    }
  }

  componentDidMount () {
    this.initData()
    this.calculateMetrics()
  }

  componentDidUpdate (prevProps) {
    if (
      (prevProps.highlightSlice[0] !== this.props.highlightSlice[0]) ||
      (prevProps.highlightSlice[1] !== this.props.highlightSlice[1])
    ) {
      this._throttledCalculateMetrics()
    }
  }

  initData () {
    // init rwgps objects
    const { route } = this.props
    const tpsResult = window.rwgps.TrackPoint.fromJSON(route.rawPoints)
    const points = tpsResult.trackPoints
    const data = new window.rwgps.Data(points)
    this._data = data
  }

  calculateMetrics = () => {
    const { highlightSlice } = this.props
    // old RSP includes the last point in the highlight slice when it does metrics calculation
    const dataSlice = this._data.slice(highlightSlice[0], highlightSlice[1] - highlightSlice[0] + 1)
    const mc = new window.rwgps.MetricsCalculator(dataSlice, false)
    const metrics = mc.calculate()
    this.setState({ metrics })
  }

  render () {
    const { route, highlightSlice, label, labelPrefix, mapZoomed, onZoomClicked, onRequestClose } = this.props
    const { metrics } = this.state
    if (!metrics) { return null } // wait until we have metrics for first render to prevent content jumping
    const slicePoints = route.trackPoints.slice(highlightSlice[0], highlightSlice[1])
    const firstPoint = route.trackPoints[highlightSlice[0]]
    const lastPoint = route.trackPoints[highlightSlice[1]]
    const position = northernmostPoint(slicePoints)

    const headerText = label || <>
      {labelPrefix || <Msg m={messages.selected} />}: <Distance value={firstPoint.distance} short bigUnits valueOnly /> – <Distance value={lastPoint.distance} short bigUnits />
      </>

    return (
      <Popup
        header={headerText}
        position={position}
        minWidth={300}
        maxWidth={300}
        zIndex={2}
        closeButton
        zoomButton={typeof mapZoomed === 'boolean' && { onZoomClicked, zoomText: <MapZoomText mapZoomed={mapZoomed} /> }}
        onRequestClose={onRequestClose}>
        <div className={styles.SegmentPopup}>
          <div className={styles.metrics}>
            <Metric label={<Msg lowerCase m={commonMetrics.distance} />} value={<Distance short value={metrics.distance} />} />
            <Metric label={<Msg m={commonMetrics.elevationGain} />} value={<Elevation short value={metrics.ele_gain} />} />
            <Metric label={<Msg m={commonMetrics.elevationLoss} />} value={<Elevation short value={metrics.ele_loss} />} />
            <Metric label={<Msg lowerCase m={commonMetrics.avgGrade} />} value={metrics.grade && <Grade short value={metrics.grade.avg || 0} />} />
            <Metric label={<Msg lowerCase m={commonMetrics.maxGrade} />} value={metrics.grade && <Grade short value={metrics.grade.max || 0} />} />
            {metrics.movingTime && <Metric label={<Msg lowerCase m={commonMetrics.movingTime} />} value={<Duration value={metrics.movingTime} biggestUnits valueOnly />} />}
            {!metrics.movingTime && <Metric label={<Msg m={featureNames.estimatedTime} />} value={<Duration value={metrics.estimatedTime || 0} biggestUnits valueOnly />} />}
          </div>
        </div>
      </Popup>
    )
  }
}

const MapZoomText = ({ mapZoomed }) => {
  const fm = useFormatMessage()
  return <>
    {mapZoomed
      ? fm(commonMessages.zoomOut, { zoomable: fm(commonNouns.map) })
      : fm(commonMessages.zoomIn, { zoomable: fm(commonNouns.map) })}
  </>
}

const Metric = ({ label, value }) => <div className={styles.metric}>
  <div className={styles.value}>{value}</div>
  <div className={styles.label}>{label}</div>
</div>
